import { OrderOperationType } from 'api/types/entity/order';
import { GridFields, InfoCard } from 'components';
import {
  convertDateByTimezone,
  formatToDateTimeShort,
  formatWithGmtOffset,
} from 'helpers';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { ChipOperationStatus } from 'modules/operation/common/components';
import { useOrderContext } from 'modules/order/order/providers/OrderProvider';

import { OrderSection } from '../../../OrderSection';

interface OrderOperationsProps {
  brokerTimezoneOffset: number;
}

export const OrderOperations = ({
  brokerTimezoneOffset,
}: OrderOperationsProps) => {
  const { t } = useTranslation();
  const { order } = useOrderContext();
  const { navigate } = useNavigate();

  const isPurchaseOrSale =
    order.operationType === OrderOperationType.SecuritiesPurchase ||
    order.operationType === OrderOperationType.SecuritiesSale;

  if (!isPurchaseOrSale) {
    return null;
  }

  const hasOperations =
    order.operations !== undefined && order.operations.length > 0;

  function handleOperationClick(operationId: string) {
    navigate(ROUTES.operations.operation.fullPath, {
      operationId,
    });
  }

  return (
    <OrderSection
      afterFields={
        hasOperations && (
          <GridFields>
            {order.operations?.map((operation) => (
              <InfoCard
                chip={
                  <ChipOperationStatus size="xs" status={operation.status} />
                }
                fields={[
                  {
                    label: t(
                      'order.sections.executionHistorySection.operations.fields.executedAt'
                    ),
                    value: formatWithGmtOffset(
                      convertDateByTimezone(
                        operation.executedAt,
                        brokerTimezoneOffset
                      ),
                      brokerTimezoneOffset,
                      formatToDateTimeShort
                    ),
                  },
                  {
                    label: t(
                      'order.sections.executionHistorySection.operations.fields.isin'
                    ),
                    value: operation.isin,
                  },
                  {
                    label: t(
                      'order.sections.executionHistorySection.operations.fields.quantity'
                    ),
                    value: String(operation.quantity),
                  },
                  {
                    label: t(
                      'order.sections.executionHistorySection.operations.fields.netUnitPrice'
                    ),
                    value: operation.netUnitPrice,
                  },
                ]}
                title={t(
                  'order.sections.executionHistorySection.operations.cardTitle',
                  {
                    number: operation.number,
                  }
                )}
                onClick={() => handleOperationClick(operation.id)}
              />
            ))}
          </GridFields>
        )
      }
      fields={[
        {
          label: t(
            'order.sections.executionHistorySection.operations.fields.executedRequested'
          ),
          value: hasOperations
            ? `${order.executedQuantity} / ${order.requestedQuantity}`
            : `0 / ${order.requestedQuantity}`,
        },
      ]}
      spacing={12}
      title={t('operation.operations.title')}
    />
  );
};
